// Blog.js - Community blog/posts list page
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogApi } from '../../Services/Api';
import styles from './Blog.module.css';

const Blog = ({ userData }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  
  // Check if user is admin
  const isAdmin = userData?.is_staff || userData?.is_superuser;
  
  // Filters from URL
  const currentCategory = searchParams.get('category') || '';
  const currentSort = searchParams.get('sort') || 'recent';
  const currentSearch = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const fetchCategories = async () => {
    try {
      const response = await blogApi.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await blogApi.getPosts({
        category: currentCategory,
        sort: currentSort,
        search: currentSearch,
        page: currentPage,
        per_page: 10
      });
      setPosts(response.data.posts);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, currentSort, currentSearch, currentPage]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when changing filters
    if (key !== 'page') {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleLike = async (postId, e) => {
    e.stopPropagation();
    try {
      const response = await blogApi.likePost(postId);
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, is_liked: response.data.liked, like_count: response.data.like_count }
          : post
      ));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
      }
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>📝 Community Blog</h1>
          <p>Share your journey, learn from others, and support the community</p>
        </div>
        <div className={styles.headerActions}>
          {isAdmin && (
            <button 
              className={styles.adminButton}
              onClick={() => navigate('/blog/admin')}
            >
              🛡️ Moderation
            </button>
          )}
          <button 
            className={styles.myPostsButton}
            onClick={() => navigate('/blog/my-posts')}
          >
            📄 My Posts
          </button>
          <button 
            className={styles.writeButton}
            onClick={() => navigate('/blog/write')}
          >
            ✍️ Write Post
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        {/* Categories */}
        <div className={styles.categoryTabs}>
          <button 
            className={`${styles.categoryTab} ${!currentCategory ? styles.active : ''}`}
            onClick={() => updateFilter('category', '')}
          >
            All Posts
          </button>
          {categories.map(cat => (
            <button 
              key={cat.slug}
              className={`${styles.categoryTab} ${currentCategory === cat.slug ? styles.active : ''}`}
              onClick={() => updateFilter('category', cat.slug)}
            >
              {cat.icon} {cat.name}
              <span className={styles.postCount}>{cat.post_count}</span>
            </button>
          ))}
        </div>

        {/* Sort & Search */}
        <div className={styles.filterRow}>
          <div className={styles.sortButtons}>
            <span>Sort by:</span>
            {[
              { value: 'recent', label: '🕐 Recent' },
              { value: 'popular', label: '👀 Popular' },
              { value: 'most_liked', label: '❤️ Most Liked' }
            ].map(sort => (
              <button
                key={sort.value}
                className={`${styles.sortButton} ${currentSort === sort.value ? styles.active : ''}`}
                onClick={() => updateFilter('sort', sort.value)}
              >
                {sort.label}
              </button>
            ))}
          </div>
          
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search posts..."
              value={currentSearch}
              onChange={(e) => updateFilter('search', e.target.value)}
              className={styles.searchInput}
            />
            {currentSearch && (
              <button 
                className={styles.clearSearch}
                onClick={() => updateFilter('search', '')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className={styles.postsList}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>📭</span>
            <h3>No posts found</h3>
            <p>Be the first to share your journey!</p>
            <button 
              className={styles.writeButtonSmall}
              onClick={() => navigate('/blog/write')}
            >
              Write a Post
            </button>
          </div>
        ) : (
          posts.map(post => (
            <article 
              key={post.id} 
              className={styles.postCard}
              onClick={() => navigate(`/blog/${post.slug}`)}
            >
              <div className={styles.postHeader}>
                {post.category && (
                  <span className={styles.postCategory}>
                    {post.category.icon} {post.category.name}
                  </span>
                )}
                <span className={styles.postDate}>{formatDate(post.published_at)}</span>
              </div>
              
              <h2 className={styles.postTitle}>{post.title}</h2>
              <p className={styles.postExcerpt}>{post.excerpt}</p>
              
              <div className={styles.postFooter}>
                <div className={styles.postAuthor}>
                  <span className={styles.authorAvatar}>
                    {post.author.username.charAt(0).toUpperCase()}
                  </span>
                  <span>{post.author.username}</span>
                </div>
                
                <div className={styles.postStats}>
                  <button 
                    className={`${styles.likeButton} ${post.is_liked ? styles.liked : ''}`}
                    onClick={(e) => handleLike(post.id, e)}
                  >
                    {post.is_liked ? '❤️' : '🤍'} {post.like_count}
                  </button>
                  <span className={styles.readMore}>Read more →</span>
                </div>
              </div>
            </article>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            disabled={currentPage === 1}
            onClick={() => updateFilter('page', currentPage - 1)}
          >
            ← Previous
          </button>
          
          <span className={styles.pageInfo}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          
          <button
            className={styles.pageButton}
            disabled={currentPage === pagination.totalPages}
            onClick={() => updateFilter('page', currentPage + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* Quick Links */}
      {/* <div className={styles.quickLinks}>
        <button onClick={() => navigate('/blog/my-posts')}>
          📄 My Posts
        </button>
      </div> */}
    </div>
  );
};

export default Blog;
